import L from "leaflet";
import type { ForteXApi, KeyValue, MapObject, ObjectLocalized } from "@fortex/api";
import type { InfoApi, MapCoreEvents } from "../core/api";
import type { Emitter } from "../core/events";
import type { LatLngTuple, ResolvedOptions } from "../core/options";
import type { InfoState, MapStore } from "../core/store";
import { errorMessage, isAbort } from "../core/support";
import { mapObjectFromFeature, mapObjectFromLocalized } from "../features/adapters";
import type { FeatureIndex } from "../features/FeatureIndex";
import { selectionColor } from "../features/style";
import type { Feature } from "../features/types";
import { geometryPoints, parseWkt } from "../geo/wkt";
import type { WktGeometry } from "../geo/wkt";
import type { Labels } from "../i18n/labels";
import type { LayerManager } from "../layers/LayerManager";
import type { InfoFeature, InfoHit, InfoProvider } from "../sources/types";
import { zIndexBands } from "../sources/types";

export const selectionPane = "fxmap-selection";

export type CloseReason = "user" | "selection" | "destroy";

export type FeatureInfoOptions = {
  map: L.Map;
  api: ForteXApi;
  store: MapStore;
  labels: Labels;
  options: ResolvedOptions["info"];
  lang: () => string;
  index: FeatureIndex;
  layers: LayerManager;
  events: Emitter<MapCoreEvents>;
};

export type FeatureInfoController = InfoApi & {
  onFeatureClick(feature: Feature, latlng: LatLngTuple): void;
  onClusterClick(feature: Feature): void;
  onMapClick(latlng: LatLngTuple): Promise<void>;
  onSelectionChange(ids: number[], objects: MapObject[]): void;
  handleEscape(): boolean;
  current(): InfoState;
  dispose(): void;
};

export function highlightLayers(geometry: WktGeometry, renderer: L.Renderer, color: string): L.Layer[] {
  const base = { pane: selectionPane, renderer, interactive: false, color, weight: 4, opacity: 0.95 };

  switch (geometry.kind) {
    case "point":
      return [L.circleMarker(geometry.point, { ...base, radius: 9, fillColor: color, fillOpacity: 0.2 })];
    case "line":
      return [L.polyline(geometry.points, base)];
    case "polygon":
      return [L.polygon(geometry.rings, { ...base, dashArray: "6 4", fillColor: color, fillOpacity: 0.1 })];
    case "multi":
      return geometry.parts.flatMap(a => highlightLayers(a, renderer, color));
  }
}

export function createFeatureInfo(settings: FeatureInfoOptions): FeatureInfoController {
  const { map, api, store, labels, options, index, layers, events } = settings;
  let controller: AbortController | null = null;
  let highlight: L.LayerGroup | null = null;
  let route: L.Polyline | null = null;
  let renderer: L.Renderer | null = null;
  let currentProvider: InfoProvider | null = null;
  let disposed = false;

  if (!map.getPane(selectionPane)) {
    const pane = map.createPane(selectionPane);

    pane.style.zIndex = String(zIndexBands.selection);
    pane.style.pointerEvents = "none";
  }

  const state = (): InfoState => store.getState().info;

  const setInfo = (info: InfoState): void => {
    if (disposed) return;

    const selected = info.kind === "tile" || info.kind === "object" || info.kind === "multi" || info.kind === "cluster" || info.kind === "loading";

    if (selected) store.setState({ info, sideTab: "selected" });
    else store.getState().setInfo(info);
  };

  const abort = (): void => {
    controller?.abort();
    controller = null;
  };

  const nextSignal = (): AbortSignal => {
    abort();
    controller = new AbortController();

    return controller.signal;
  };

  const clearRoute = (): void => {
    route?.remove();
    route = null;
  };

  const clearHighlight = (): void => {
    highlight?.remove();
    highlight = null;
    clearRoute();
  };

  const toast = (text: string): void => {
    if (!disposed) store.getState().pushToast({ severity: "danger", text });
  };

  const currentPosition = (): Promise<LatLngTuple> =>
    new Promise((resolve, reject) => {
      if (typeof navigator === "undefined" || !navigator.geolocation) {
        reject(new Error("geolocation"));
        return;
      }

      navigator.geolocation.getCurrentPosition(position => resolve([position.coords.latitude, position.coords.longitude]), reject, { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 });
    });

  const drawRoute = (points: LatLngTuple[]): void => {
    clearRoute();
    renderer ??= L.svg({ pane: selectionPane });
    route = L.polyline(points, { pane: selectionPane, renderer, interactive: false, weight: 5, opacity: 0.9, className: "fxmap-route" }).addTo(map);
    map.fitBounds(route.getBounds(), { padding: [40, 40] });
  };

  const routeTo = async (): Promise<void> => {
    const current = state();

    if (current.kind !== "tile") return;

    const goal = geometryPoints(parseWkt(current.feature.feature.wkt))[0];

    if (goal === undefined) return;

    let start: LatLngTuple;

    try {
      start = await currentPosition();
    } catch {
      toast(labels.locationUnavailable);
      return;
    }

    try {
      const result = await api.findPath(start, goal);
      const points = (result.points ?? []).map(a => [a.lat, a.lng] as LatLngTuple);

      if (disposed || state().kind !== "tile") return;
      if (points.length < 2) {
        toast(labels.routeFailed);
        return;
      }

      drawRoute(points);
    } catch (error) {
      if (isAbort(error)) return;

      toast(`${labels.routeFailed}: ${errorMessage(error, labels)}`);
    }
  };

  const drawHighlight = (wkt: string | null): void => {
    clearHighlight();

    const geometry = parseWkt(wkt);

    if (geometry === null) return;

    renderer ??= L.svg({ pane: selectionPane });
    highlight = L.layerGroup(highlightLayers(geometry, renderer, selectionColor)).addTo(map);
  };

  const close = (reason: CloseReason = "user"): void => {
    const wasOpen = state().kind !== "closed";

    abort();
    clearHighlight();
    currentProvider = null;

    if (!wasOpen) return;

    setInfo({ kind: "closed" });
    events.emit("info:close", { reason });
  };

  const providerFor = (layer: string): InfoProvider | null => layers.sourceByLayerName(layer)?.info ?? null;

  const openObjectAsync = async (object: MapObject): Promise<void> => {
    if (!options.enabled || object.memberCount > 1) return;

    const existing = state();

    if ((existing.kind === "object" && existing.object.id === object.id) || (existing.kind === "loading" && existing.objectId === object.id)) return;

    const signal = nextSignal();

    clearHighlight();
    currentProvider = null;
    setInfo({ kind: "loading", objectId: object.id, hit: null });
    events.emit("info:open", { object });

    try {
      const detail: ObjectLocalized = await api.objectLocalized(object.id, settings.lang(), signal);

      if (signal.aborted) return;

      setInfo({ kind: "object", object: { ...object, name: object.name ?? detail.name, wkt: object.wkt ?? detail.wkt }, detail, error: null });
    } catch (error) {
      if (signal.aborted || isAbort(error)) return;

      setInfo({ kind: "object", object, detail: null, error: errorMessage(error, labels) });
    }
  };

  const openObject = (object: MapObject): void => {
    void openObjectAsync(object);
  };

  const open = async (objectId: number): Promise<void> => {
    const feature = index.byObjectId(objectId);
    const loaded = feature === null ? null : mapObjectFromFeature(feature);

    if (loaded !== null) {
      await openObjectAsync(loaded);
      return;
    }

    const signal = nextSignal();

    setInfo({ kind: "loading", objectId, hit: null });

    try {
      const detail = await api.objectLocalized(objectId, settings.lang(), signal);

      if (signal.aborted) return;

      const entry = layers.catalogueEntries().find(a => a.kind === "geoobject" && a.source?.typeIdName !== null && detail.type_Id !== null && a.id === detail.type_Id) ?? null;
      const object = mapObjectFromLocalized(detail, entry);

      setInfo({ kind: "object", object, detail, error: null });
      events.emit("info:open", { object });
    } catch (error) {
      if (signal.aborted || isAbort(error)) return;

      setInfo({ kind: "error", message: errorMessage(error, labels) });
    }
  };

  const openHit = async (hit: InfoHit, latlng: LatLngTuple, zoom: number, searchTerm: string | null = null): Promise<void> => {
    if (!options.enabled) return;

    if (hit.feature?.object) {
      openObject(hit.feature.object);
      return;
    }

    const provider = providerFor(hit.layer);

    if (provider === null) {
      setInfo({ kind: "error", message: labels.featureLoadFailed });
      return;
    }

    const signal = nextSignal();

    currentProvider = provider;
    setInfo({ kind: "loading", objectId: null, hit });
    events.emit("info:open", { object: null });

    try {
      const feature = await provider.feature(hit, latlng, zoom, signal);

      if (signal.aborted) return;

      drawHighlight(feature.feature.wkt);
      setInfo({ kind: "tile", feature, dirty: false, saving: "idle", error: null, searchTerm });
    } catch (error) {
      if (signal.aborted || isAbort(error)) return;

      setInfo({ kind: "error", message: `${labels.featureLoadFailed}: ${errorMessage(error, labels)}` });
    }
  };

  const openFeature = (layer: string, id: string, searchTerm: string | null = null): Promise<void> => {
    const center = map.getCenter();

    return openHit({ id, layer, name: "", feature: null }, [center.lat, center.lng], map.getZoom(), searchTerm);
  };

  const saveFeature = async (data: KeyValue[]): Promise<void> => {
    const current = state();

    if (current.kind !== "tile" || currentProvider === null || !currentProvider.save) return;

    setInfo({ ...current, saving: "saving", error: null });

    try {
      const updated: InfoFeature = await currentProvider.save(current.feature.hit, data);

      if (disposed) return;

      drawHighlight(updated.feature.wkt);
      setInfo({ kind: "tile", feature: updated, dirty: false, saving: "saved", error: null, searchTerm: current.searchTerm ?? null });
    } catch (error) {
      if (disposed) return;

      setInfo({ ...current, saving: "error", error: `${labels.featureSaveFailed}: ${errorMessage(error, labels)}` });
    }
  };

  const onMapClick = async (latlng: LatLngTuple): Promise<void> => {
    if (!options.enabled) return;

    const providers = layers.infoSources().filter(a => a.source.features === null && (options.layers === null || options.layers.includes(a.entry.entry.idName)));

    if (providers.length === 0) return;

    const signal = nextSignal();
    const zoom = map.getZoom();

    try {
      const results = await Promise.all(providers.map(a => a.source.info!.hitTest(latlng, zoom, signal).catch(() => [] as InfoHit[])));

      if (signal.aborted) return;

      const hits = results.flat();

      if (hits.length === 0) return;
      if (hits.length === 1) {
        await openHit(hits[0], latlng, zoom);
        return;
      }

      currentProvider = null;
      clearHighlight();

      if (state().kind !== "closed") {
        setInfo({ kind: "closed" });
        events.emit("info:close", { reason: "user" });
      }

      store.setState({ results: { source: "click", term: "", items: hits.map(hit => ({ kind: "feature", hit, latlng, zoom })), capped: [], loading: false, error: null }, sideTab: "results" });
    } catch (error) {
      if (signal.aborted || isAbort(error)) return;

      setInfo({ kind: "error", message: `${labels.featureLoadFailed}: ${errorMessage(error, labels)}` });
    }
  };

  return {
    open,
    openObject,
    openHit,
    openFeature,
    saveFeature,
    routeTo,
    close: () => close("user"),
    isOpen: () => state().kind !== "closed",
    onFeatureClick: (feature, latlng) => {
      if (feature.cluster) return;

      const object = mapObjectFromFeature(feature);

      if (object !== null && feature.object !== null) {
        openObject(object);
        return;
      }

      void openHit({ id: feature.id, layer: layers.entryOf(feature.layerId)?.idName ?? feature.layerId, name: feature.label ?? feature.id, feature }, latlng, map.getZoom());
    },
    onClusterClick: feature => {
      abort();
      clearHighlight();
      setInfo({ kind: "cluster", feature });
    },
    onMapClick,
    onSelectionChange: (ids, objects) => {
      if (ids.length === 0) {
        const current = state();

        if (current.kind === "object" || current.kind === "multi" || (current.kind === "loading" && current.objectId !== null)) close("selection");

        return;
      }

      if (ids.length >= 2) {
        abort();
        clearHighlight();
        setInfo({ kind: "multi", objects });
        return;
      }

      if (objects.length === 1) openObject(objects[0]);
    },
    handleEscape: () => {
      if (state().kind === "closed") return false;

      close("user");

      return true;
    },
    current: state,
    dispose: () => {
      close("destroy");
      disposed = true;
    }
  };
}
